import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding Recap #45: Almost building a ballistic missile with React and AR.js",
  "description": "",
  "published": "2017-09-04T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-recap-45-almost-building-ballistic-missile-react-ar-js/swizec/7773"],
  "categories": "arjs, JavaScript, Livecoding, react, reactjs, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should follow `}<a parentName="em" {...{
          "href": "https://www.youtube.com/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <lite-youtube {...{
      "videoid": "39jb-y85YBo",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`This Sunday we had 3 jobs:`}</p>
    <ol>
      <li parentName="ol">{`Get the AR Minecraft guy working ✅`}</li>
      <li parentName="ol">{`Use a hand drawn custom marker ❌`}</li>
      <li parentName="ol">{`Fire an AR ballistic missile from Korea ✅❌`}</li>
    </ol>
    <p>{`But AR is hard.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`AR is hard `}<a parentName="p" {...{
          "href": "https://t.co/SDwTMPjxJw"
        }}>{`pic.twitter.com/SDwTMPjxJw`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/904492731086528512"
      }}>{`September 3, 2017`}</a></blockquote>
    <p>{`We got pretty close, just like I suggested in `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/livecoding-recap-44-dipping-toes-arjs/swizec/7759"
      }}>{`last week's Livecoding Recap`}</a>{` the Minecraft guy just needed some textures. After we added those, he became visible and looked great.`}</p>
    <p>{`Here he is next to Kiwi.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/09/ar-stuff-1024x576.jpg",
        "alt": null
      }}></img></p>
    <p>{`To get that working we learned about `}<a parentName="p" {...{
        "href": "https://A-FRAME.io/docs/0.6.0/core/component.html#schema"
      }}>{`A-FRAME Schemas`}</a>{`. They're a way to describe the properties that an A-FRAME component can accept. A lot like React's propTypes, but as an object.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`minecraft`}</inlineCode>{` component comes from `}<inlineCode parentName="p">{`A-FRAME-minecraft.js`}</inlineCode>{`, which we copied off examples in `}<a parentName="p" {...{
        "href": "https://github.com/jeromeetienne/AR.js"
      }}>{`Jerome Etiennes AR.js repo`}</a>{`. That looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`FRAME.registerComponent("minecraft", {
  schema: {
    skinUrl: {
      type: "string",
      default: "",
    },
    wellKnownSkin: {
      type: "string",
      default: "",
    },
    heightMeter: {
      default: 1,
    },
  },
  init: function () {
    var character = new THREEx.MinecraftChar();
    this.character = character;

    this.el.object3D.add(character.root);
    // this.el.setObject3D('superRoot', character.root);
  },
  update: function () {
    if (Object.keys(this.data).length === 0) return;
    var character = this.character;
    character.root.scale.set(1, 1, 1).multiplyScalar(this.data.heightMeter);

    if (this.data.skinUrl) {
      character.loadSkin(this.data.skinUrl);
    } else if (this.data.wellKnownSkin) {
      character.loadWellKnownSkin(this.data.wellKnownSkin);
    }
  },
});
`}</code></pre>
    <p>{`I have no idea which component system this is modeled after, but it works. Looks like we can define minecraft guy's height in meters, a URL for the skin texture, and choose one of well known skins. Those won't work in our project because we don't have the files.`}</p>
    <p>{`Schema props are passed into A-FRAME components as a string. Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<entity minecraft="{\`heightMeter:" 2;="" skinurl:="" \${skinurl}\`}="" minecraft-head-anim="yes" minecraft-body-anim="hiwave">
</entity>
`}</code></pre>
    <p>{`You can read that as `}{`*`}{`"Create an A-FRAME Entity composed out of `}<inlineCode parentName="p">{`minecraft`}</inlineCode>{` with `}<inlineCode parentName="p">{`heightMeter: 2; skinUrl:`}</inlineCode>{`, and `}<inlineCode parentName="p">{`minecraft-head-anim`}</inlineCode>{` set to `}<inlineCode parentName="p">{`yes`}</inlineCode>{` and `}<inlineCode parentName="p">{`minecraft-body-anim`}</inlineCode>{` set to `}<inlineCode parentName="p">{`hiwave`}</inlineCode>{`. Both `}<inlineCode parentName="p">{`minecraft-head-anim`}</inlineCode>{` and `}<inlineCode parentName="p">{`minecraft-body-anim`}</inlineCode>{` are A-FRAME components.`}</p>
    <p>{`I don't know yet how that composition works, but it looks neat.`}</p>
    <p>{`Equipped with a working Minecraft demo dude, we went on to try creating our own marker from scratch. According to the AR.js repo, you are to use this `}<a parentName="p" {...{
        "href": "http://flash.tarotaro.org/blog/2009/07/12/mgo2/"
      }}>{`Flash-based marker training`}</a>{` implementation from 2009.`}</p>
    <p>{`Flash doesn't work well in 2017. Couldn't get it to run at all in Chrome and Firefox wouldn't let us click on "Give camera permissions". Had to hack around that and manually add a rule to the global permissions table.`}</p>
    <p>{`We were able to create a marker file. The trick is to have an enclosed area with straight lines. You can put any sort of scribble inside, as long as the outside is straight and enclosed.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/09/Screen-Shot-2017-09-03-at-19.25.21.png",
        "alt": "Custom Marker"
      }}></img></p>
    <p>{`Unfortunately we weren't able to convince AR.js to use our marker. Even explicitly setting the marker URL didn't work and it kept using default settings.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<a-marker type="pattern" url="/markers/marker64.pat"></a-marker>
`}</code></pre>
    <p>{`¯`}{`\\`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <p>{`Onwards to making a ballistic missile!`}</p>
    <p>{`We went the `}<a parentName="p" {...{
        "href": "https://github.com/ngokevin/aframe-react"
      }}>{`aframe-react`}</a>{` way to build our ballistic missile. That way we could postpone understanding true A-FRAME components a little longer.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class BallisticMissile extends Component {

    render() {
        const { pos } = this.props;

        return (
            <entity geometry="{{primitive:" 'box',="" width:="" .2,="" height:="" 1,="" depth:="" .2}}="" material="{{roughness:" 0,="" src:="" require('.="" texture.jpg')}}="" scale="{{x:" y:="" z:="" 1}}="" position="{pos}<p">

                    />
        )
    }
}
</entity>
`}</code></pre>
    <p><inlineCode parentName="p">{`BallisticMissile`}</inlineCode>{` is a class component because we wanted to use a `}<inlineCode parentName="p">{`tickHandler`}</inlineCode>{` to animate as `}<a parentName="p" {...{
        "href": "https://github.com/ngokevin/aframe-react#best-practices"
      }}>{`aframe-react README`}</a>{` suggests.`}</p>
    <blockquote>
      <p parentName="blockquote">{`For example, don't create requestAnimationFrames to continuously throw React prop updates to A-Frame. Instead, create an A-Frame component with a tick handler such that everything is done in memory with little overhead.`}</p>
    </blockquote>
    <p>{`This did not work. I couldn't figure out how to set a tick handler that actually gets called, so we set up a little game loop in our \`\`component to make the missile fly.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class App extends Component {
    state = {
        missile: false,
        launching: false
    }

    launch = () => {
        this.setState({
            missile: true,
            launching: true,
            missilePos: { x: 0, y: 0, z: 0 },
            missileA: { ax: 0, ay: .005, az: 0 },
            missileV: { vx: 0, vy: 0, vz: 0 }
        });
        if (!this.launched) {
            window.requestAnimationFrame(() => this.gameLoop());
            this.launched = true;
        }
    }

    gameLoop() {
        const { x, y, z } = this.state.missilePos,
              { vx, vy, vz } = this.state.missileV,
              { ax, ay, az } = this.state.missileA;

        this.setState({
            missilePos: { x: x+vx, y: y+vy, z: z+vz },
            missileV: { vx: vx+ax, vy: vy+ay, vz: vz+az }
        });
        window.requestAnimationFrame(() => this.gameLoop());
    }
`}</code></pre>
    <p>{`On every `}<inlineCode parentName="p">{`requestAnimationFrame`}</inlineCode>{` we update `}<inlineCode parentName="p">{`missilePos`}</inlineCode>{` with velocity on each axis, and `}<inlineCode parentName="p">{`missileV`}</inlineCode>{` with acceleration for each axis. This creates a missile that flies straight up and never stops.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`AR is hard `}<a parentName="p" {...{
          "href": "https://t.co/SDwTMPjxJw"
        }}>{`pic.twitter.com/SDwTMPjxJw`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/904492731086528512"
      }}>{`September 3, 2017`}</a></blockquote>
    <p>{`You can try launching a missile `}<a parentName="p" {...{
        "href": "https://swizec.github.io/ar-js-experiment/"
      }}>{`here`}</a>{`. You'll need the `}<a parentName="p" {...{
        "href": "https://jeromeetienne.github.io/AR.js/data/images/HIRO.jpg"
      }}>{`hiro marker`}</a>{`.`}</p>
    <p>{`Join me next time as we try to make the missile look better and convince it to fly in a ballistic trajectory.`}</p>
    <p>{`🚀`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      